/**
 * @file FilterRow.tsx
 * @desc Individual filter row component
 * @copyright All rights reserved @ Inarix
 */
import React from "react";
import {
  Box,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Autocomplete,
} from "@mui/material";
import { TFunction } from "i18next";
import { customFilterFormStyles } from "../../dataTable/table/customFilterForm/CustomFilterFormStyles";
import { FilterItemExtended } from "../types";
import {
  getTranslatedOperatorLabel,
  getAvailableOperators,
  getFilterableColumns,
  getChipValues,
  formatOptionLabel,
} from "../utils/filterUtils";

interface FilterRowProps {
  filterItem: FilterItemExtended;
  index: number;
  columns: any[];
  t: TFunction<"global", undefined>;
  apiOptions: { [key: number]: any[] };
  inputValues: { [key: number]: string };
  loadingFilters: boolean;
  statusOptions: Array<{
    value: string;
    label: string;
    id: number | string;
    description: string;
  }>;
  comesFromDashboard?: boolean;
  onFieldChange: (index: number, field: string) => void;
  onOperatorChange: (index: number, operator: string) => void;
  onValueChange: (index: number, value: string) => void;
  onChipValueChange: (index: number, values: string[]) => void;
  onInputValueChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  filterItemsLength: number;
}

export const FilterRow: React.FC<FilterRowProps> = ({
  filterItem,
  index,
  columns,
  t,
  apiOptions,
  inputValues,
  loadingFilters,
  statusOptions,
  comesFromDashboard,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onChipValueChange,
  onInputValueChange,
  onRemove,
  filterItemsLength,
}) => {
  return (
    <Box
      key={`filter-${index}-${filterItem.field}`}
      sx={customFilterFormStyles.filterRow}
    >
      <Button
        onClick={() => onRemove(index)}
        disabled={filterItemsLength === 0}
        sx={customFilterFormStyles.deleteButton}
      >
        ✕
      </Button>

      <Select
        value={filterItem.field}
        onChange={(e) => onFieldChange(index, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        displayEmpty
        size="small"
        variant="standard"
        sx={customFilterFormStyles.select}
      >
        <MenuItem value="">
          <em style={customFilterFormStyles.placeholderText}>
            {t("custom_filter.select_field")}
          </em>
        </MenuItem>
        {getFilterableColumns(columns).map((column) => (
          <MenuItem key={column.field} value={column.field}>
            {column.headerName || column.field}
          </MenuItem>
        ))}
      </Select>

      <Select
        value={filterItem.operator || ""}
        onChange={(e) => onOperatorChange(index, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        disabled={!filterItem.field}
        displayEmpty
        size="small"
        variant="standard"
        sx={customFilterFormStyles.select}
      >
        <MenuItem value="">
          <em style={customFilterFormStyles.placeholderText}>
            {t("custom_filter.operator")}
          </em>
        </MenuItem>
        {getAvailableOperators(filterItem.field, columns).map((operator) => (
          <MenuItem key={operator.value} value={operator.value}>
            {getTranslatedOperatorLabel(operator.value, t)}
          </MenuItem>
        ))}
      </Select>

      {filterItem.field === "computedStatusId" ||
      filterItem.field === "licensesStatus" ? (
        <Select
          value={filterItem.value || ""}
          onChange={(e) => onValueChange(index, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          disabled={!filterItem.operator}
          displayEmpty
          size="small"
          variant="standard"
          sx={customFilterFormStyles.select}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
              },
            },
          }}
        >
          <MenuItem value="">
            <em style={customFilterFormStyles.placeholderText}>
              {t("custom_filter.select_status")}
            </em>
          </MenuItem>
          {statusOptions.map((option) => (
            <MenuItem
              key={option.id}
              value={option.id.toString()}
              title={option.description || ""}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      ) : filterItem.realOperator === "$in" ? (
        (() => {
          const column = columns.find((col) => col.field === filterItem.field);
          const isApiFilter = column?.filter === "API";
          const isLocalFilter = column?.filter === "LOCAL";
          const isDynamicFilter = isApiFilter || isLocalFilter;
          const apiOptionsForIndex = apiOptions[index] || [];
          const isActionsFilter = column?.service === "actions";

          return (
            <Autocomplete
              multiple
              freeSolo
              loading={isDynamicFilter && loadingFilters}
              loadingText={t("custom_filter.loading")}
              options={
                isDynamicFilter
                  ? apiOptionsForIndex.map((opt) =>
                      formatOptionLabel(opt, isActionsFilter),
                    )
                  : []
              }
              filterOptions={(options, state) => {
                if (isDynamicFilter) {
                  return options.filter((option) =>
                    option
                      .toLowerCase()
                      .includes(state.inputValue.toLowerCase()),
                  );
                }
                return [];
              }}
              value={getChipValues(filterItem.value)}
              inputValue={inputValues[index] || ""}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  onInputValueChange(index, newInputValue);
                }
              }}
              onChange={(event, newValue, reason) => {
                event?.stopPropagation();

                if (
                  reason === "selectOption" ||
                  reason === "removeOption" ||
                  reason === "clear" ||
                  reason === "createOption"
                ) {
                  onChipValueChange(index, newValue);
                  onInputValueChange(index, "");
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, tagIndex) => {
                  const displayLabel =
                    option.length > 20
                      ? `${option.substring(0, 20)}...`
                      : option;
                  return (
                    <Chip
                      variant="outlined"
                      label={displayLabel}
                      title={option}
                      size="small"
                      {...getTagProps({ index: tagIndex })}
                      key={`${option}-${tagIndex}`}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder={t("custom_filter.filter_value")}
                  size="small"
                  disabled={!filterItem.realOperator}
                  sx={customFilterFormStyles.textField}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              componentsProps={{
                popper: {
                  onClick: (e: React.MouseEvent) => e.stopPropagation(),
                  sx: {
                    "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
                      fontSize: "0.80rem",
                      padding: "4px 8px",
                    },
                  },
                },
              }}
              sx={{ width: "100%" }}
            />
          );
        })()
      ) : (
        <TextField
          placeholder={t("custom_filter.filter_value")}
          value={filterItem.value}
          variant="standard"
          onChange={(e) => onValueChange(index, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          size="small"
          disabled={!filterItem.operator}
          multiline
          maxRows={4}
          sx={{
            ...customFilterFormStyles.textField,
          }}
        />
      )}
    </Box>
  );
};
